/**
 * View configuration management: defaults, reconciliation, validation, dirty tracking.
 *
 * Pure logic with no React or DOM dependencies.
 */

import type {
  Schema,
  ViewConfig,
  ColumnLayout,
  Density,
  NewColumnsPolicy,
  Filter,
  AppliedView,
  BuiltInDefault,
  ApplyViewConfig,
  ParseViewConfig,
  IsDirty,
  ViewSnapshot,
} from './types';

export const VIEW_CONFIG_VERSION = 1;

/**
 * Build the built-in default view config for a schema.
 */
export const builtInDefault: BuiltInDefault = (
  schema: Schema,
  opts: { defaultPinned: string[]; scope: string }
): ViewConfig => {
  // Everything visible in schema order
  const order = schema.columns.map(c => c.id);

  // Pinned: only those from defaultPinned that exist in the schema
  const pinned = opts.defaultPinned.filter(id => schema.byId.has(id));

  return {
    version: VIEW_CONFIG_VERSION,
    scope: opts.scope,
    savedAt: new Date().toISOString(),
    layout: {
      order,
      hidden: [],
      pinned,
      collapsedGroups: [],
      widths: {},
    },
    density: 'compact',
    newColumns: 'show',
  };
};

/**
 * Apply a saved view config to a (possibly different) schema.
 *
 * Reconciliation:
 * 1. Drop ids not in the schema from order/hidden/pinned/widths/collapsedGroups.
 * 2. Append schema columns not mentioned in order or pinned to the end of order.
 * 3. If newColumns === 'hide', also add those appended columns to hidden.
 * 4. Drop filters whose column is absent, with a notice.
 */
export const applyViewConfig: ApplyViewConfig = (
  cfg: ViewConfig,
  schema: Schema
): AppliedView => {
  const notices: string[] = [];

  // 1. Filter layout arrays to only include ids that exist in the schema
  const validOrder = cfg.layout.order.filter(id => schema.byId.has(id));
  const validHidden = cfg.layout.hidden.filter(id => schema.byId.has(id));
  const validPinned = cfg.layout.pinned.filter(id => schema.byId.has(id));
  const validCollapsedGroups = cfg.layout.collapsedGroups.filter(id =>
    schema.groupById.has(id)
  );

  // For widths: keep entries for existing columns AND existing collapsed groups
  const validWidths: Record<string, number> = {};
  for (const [id, width] of Object.entries(cfg.layout.widths)) {
    if (schema.byId.has(id) || schema.groupById.has(id)) {
      validWidths[id] = width;
    }
  }

  // 2. Find columns not mentioned in order or pinned, append them to order
  const mentionedIds = new Set([...validOrder, ...validPinned]);
  const newColumns: string[] = [];
  for (const col of schema.columns) {
    if (!mentionedIds.has(col.id)) {
      newColumns.push(col.id);
      validOrder.push(col.id);
    }
  }

  // 3. If newColumns policy is 'hide', add the new columns to hidden
  const finalHidden = [...validHidden];
  if (cfg.newColumns === 'hide') {
    for (const id of newColumns) {
      if (!finalHidden.includes(id)) {
        finalHidden.push(id);
      }
    }
  }

  // 4. Filter out filters whose column is absent
  const validFilters: Filter[] = [];
  if (cfg.filters) {
    for (const filter of cfg.filters) {
      if (schema.byId.has(filter.column)) {
        validFilters.push(filter);
      } else {
        notices.push(`Skipped filter on "${filter.column}" — not in this sample.`);
      }
    }
  }

  return {
    layout: {
      order: validOrder,
      hidden: finalHidden,
      pinned: validPinned,
      collapsedGroups: validCollapsedGroups,
      widths: validWidths,
    },
    density: cfg.density,
    newColumns: cfg.newColumns,
    filters: validFilters,
    notices,
  };
};

/**
 * Parse and validate a ViewConfig from raw data (object or JSON string).
 *
 * Returns null on ANY violation; never throws.
 */
export const parseViewConfig: ParseViewConfig = (
  raw: unknown,
  scope: string
): ViewConfig | null => {
  try {
    // If it's a string, parse it as JSON
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (typeof obj !== 'object' || obj === null) {
      return null;
    }

    const cfg = obj as Record<string, unknown>;

    // Validate version
    if (cfg.version !== VIEW_CONFIG_VERSION) {
      return null;
    }

    // Validate scope
    if (cfg.scope !== scope) {
      return null;
    }

    // Validate savedAt is a valid ISO string
    if (typeof cfg.savedAt !== 'string' || !isValidISODate(cfg.savedAt)) {
      return null;
    }

    // Validate density
    if (cfg.density !== 'compact' && cfg.density !== 'comfortable') {
      return null;
    }

    // Validate newColumns
    if (cfg.newColumns !== 'show' && cfg.newColumns !== 'hide') {
      return null;
    }

    // Validate layout
    if (typeof cfg.layout !== 'object' || cfg.layout === null) {
      return null;
    }

    const layout = cfg.layout as Record<string, unknown>;

    // Validate layout.order
    if (!isStringArray(layout.order, 2000)) {
      return null;
    }

    // Validate layout.hidden
    if (!isStringArray(layout.hidden, 2000)) {
      return null;
    }

    // Validate layout.pinned
    if (!isStringArray(layout.pinned, 20)) {
      return null;
    }

    // Validate layout.collapsedGroups
    if (!isStringArray(layout.collapsedGroups, 500)) {
      return null;
    }

    // Validate layout.widths
    if (typeof layout.widths !== 'object' || layout.widths === null || Array.isArray(layout.widths)) {
      return null;
    }
    const widths = layout.widths as Record<string, unknown>;
    for (const [key, value] of Object.entries(widths)) {
      if (typeof key !== 'string' || typeof value !== 'number') {
        return null;
      }
      if (value < 40 || value > 2000) {
        return null;
      }
    }

    // Validate filters (optional)
    let filters: Filter[] | undefined;
    if (cfg.filters !== undefined) {
      if (!Array.isArray(cfg.filters) || cfg.filters.length > 50) {
        return null;
      }

      filters = [];
      for (const f of cfg.filters) {
        const validatedFilter = validateFilter(f);
        if (!validatedFilter) {
          return null;
        }
        filters.push(validatedFilter);
      }
    }

    // All validations passed
    return {
      version: VIEW_CONFIG_VERSION,
      scope: cfg.scope as string,
      savedAt: cfg.savedAt as string,
      layout: {
        order: layout.order as string[],
        hidden: layout.hidden as string[],
        pinned: layout.pinned as string[],
        collapsedGroups: layout.collapsedGroups as string[],
        widths: widths as Record<string, number>,
      },
      density: cfg.density as Density,
      newColumns: cfg.newColumns as NewColumnsPolicy,
      filters,
    };
  } catch {
    return null;
  }
};

/**
 * Check if current view differs from baseline.
 *
 * Order and pinned are order-sensitive; hidden and collapsedGroups are sets.
 * Widths are equal within ±2px; missing widths are treated as undefined on both sides.
 * Filters (when includeFilters is true) are compared by their structure without id, order-insensitive.
 */
export const isDirty: IsDirty = (
  current: ViewSnapshot,
  baseline: ViewSnapshot,
  includeFilters: boolean
): boolean => {
  // Compare order (order-sensitive)
  if (!arraysEqual(current.layout.order, baseline.layout.order)) {
    return true;
  }

  // Compare pinned (order-sensitive)
  if (!arraysEqual(current.layout.pinned, baseline.layout.pinned)) {
    return true;
  }

  // Compare hidden (as sets)
  if (!setsEqual(new Set(current.layout.hidden), new Set(baseline.layout.hidden))) {
    return true;
  }

  // Compare collapsedGroups (as sets)
  if (!setsEqual(new Set(current.layout.collapsedGroups), new Set(baseline.layout.collapsedGroups))) {
    return true;
  }

  // Compare widths (with ±2px tolerance)
  if (!widthsEqual(current.layout.widths, baseline.layout.widths)) {
    return true;
  }

  // Compare density
  if (current.density !== baseline.density) {
    return true;
  }

  // Compare filters if requested
  if (includeFilters) {
    if (!filtersEqual(current.filters, baseline.filters)) {
      return true;
    }
  }

  return false;
};

/**
 * Convert an AppliedView to a ViewConfig.
 */
export function toViewConfig(
  applied: {
    layout: ColumnLayout;
    density: Density;
    newColumns: NewColumnsPolicy;
    filters: Filter[];
  },
  scope: string,
  includeFilters: boolean
): ViewConfig {
  return {
    version: VIEW_CONFIG_VERSION,
    scope,
    savedAt: new Date().toISOString(),
    layout: applied.layout,
    density: applied.density,
    newColumns: applied.newColumns,
    filters: includeFilters && applied.filters.length > 0 ? applied.filters : undefined,
  };
}

/**
 * Serialize a ViewConfig to pretty JSON.
 */
export function serializeViewConfig(cfg: ViewConfig): string {
  return JSON.stringify(cfg, null, 2);
}

// ---------- Helpers ----------

function isValidISODate(s: string): boolean {
  const d = new Date(s);
  return !isNaN(d.getTime()) && d.toISOString() === s;
}

function isStringArray(value: unknown, maxItems: number): value is string[] {
  if (!Array.isArray(value) || value.length > maxItems) {
    return false;
  }
  return value.every(item => typeof item === 'string');
}

function validateFilter(f: unknown): Filter | null {
  if (typeof f !== 'object' || f === null) {
    return null;
  }

  const filter = f as Record<string, unknown>;

  // Common fields
  if (typeof filter.id !== 'string' || typeof filter.column !== 'string') {
    return null;
  }

  const kind = filter.kind;
  if (typeof kind !== 'string') {
    return null;
  }

  // Validate by kind
  switch (kind) {
    case 'contains':
      if (typeof filter.text !== 'string') return null;
      if (filter.regex !== undefined && typeof filter.regex !== 'boolean') return null;
      return {
        id: filter.id,
        column: filter.column,
        kind: 'contains',
        text: filter.text,
        regex: filter.regex as boolean | undefined,
      };

    case 'in':
      if (!Array.isArray(filter.values)) return null;
      if (!filter.values.every(v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
        return null;
      }
      return {
        id: filter.id,
        column: filter.column,
        kind: 'in',
        values: filter.values as (string | number | boolean)[],
      };

    case 'range':
      if (filter.min !== undefined && typeof filter.min !== 'number') return null;
      if (filter.max !== undefined && typeof filter.max !== 'number') return null;
      return {
        id: filter.id,
        column: filter.column,
        kind: 'range',
        min: filter.min as number | undefined,
        max: filter.max as number | undefined,
      };

    case 'dateRange':
      if (filter.from !== undefined && typeof filter.from !== 'string') return null;
      if (filter.to !== undefined && typeof filter.to !== 'string') return null;
      return {
        id: filter.id,
        column: filter.column,
        kind: 'dateRange',
        from: filter.from as string | undefined,
        to: filter.to as string | undefined,
      };

    case 'missing':
      if (typeof filter.present !== 'boolean') return null;
      return {
        id: filter.id,
        column: filter.column,
        kind: 'missing',
        present: filter.present,
      };

    case 'arrayElement':
      if (typeof filter.elementPath !== 'string') return null;
      if (filter.op !== 'eq' && filter.op !== 'contains') return null;
      if (typeof filter.value !== 'string') return null;
      return {
        id: filter.id,
        column: filter.column,
        kind: 'arrayElement',
        elementPath: filter.elementPath,
        op: filter.op as 'eq' | 'contains',
        value: filter.value,
      };

    case 'arrayLength':
      if (filter.min !== undefined && typeof filter.min !== 'number') return null;
      if (filter.max !== undefined && typeof filter.max !== 'number') return null;
      return {
        id: filter.id,
        column: filter.column,
        kind: 'arrayLength',
        min: filter.min as number | undefined,
        max: filter.max as number | undefined,
      };

    default:
      return null;
  }
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

function widthsEqual(a: Record<string, number>, b: Record<string, number>): boolean {
  // Get all keys from both objects
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of allKeys) {
    const aVal = a[key];
    const bVal = b[key];

    // Both undefined/missing → equal
    if (aVal === undefined && bVal === undefined) {
      continue;
    }

    // One defined, one undefined → not equal
    if (aVal === undefined || bVal === undefined) {
      return false;
    }

    // Both defined → check tolerance
    if (Math.abs(aVal - bVal) > 2) {
      return false;
    }
  }

  return true;
}

function filtersEqual(a: Filter[], b: Filter[]): boolean {
  if (a.length !== b.length) return false;

  // Normalize filters by removing id and sorting by a stable key
  const normalize = (filters: Filter[]): string[] => {
    return filters.map(f => {
      const { id, ...rest } = f;
      return JSON.stringify(rest);
    }).sort();
  };

  const aNorm = normalize(a);
  const bNorm = normalize(b);

  return arraysEqual(aNorm, bNorm);
}
